import React, { FC, useContext, useState } from 'react';

import { Typography } from '@mui/material';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { UserInfoCtx } from '../../../../containers/CommonLayout/CommonLayout';
import styled from '../../../../styled';
import { colorsPalette, useIsWidthUp } from '../../../../theme';
import { PROFILE_LINKS } from '../../const';
import useStyles from './styles';

interface UserInfoDropdownProps {
  teamName?: string | null;
  scrolled: boolean;
  handleOpenUserComWidget: () => void;
  handleLogout: () => void;
  onClick: () => void;
}

const avatarColors = [
  { ids: ['0', '1'], background: colorsPalette.coral, color: colorsPalette.white },
  { ids: ['2', '3'], background: colorsPalette.blue, color: colorsPalette.white },
  { ids: ['4', '5'], background: colorsPalette.blueLight, color: colorsPalette.white },
  { ids: ['6', '7'], background: colorsPalette.yellow, color: colorsPalette.blackSecondary },
  { ids: ['8', '9'], background: colorsPalette.pink, color: colorsPalette.blackSecondary },
];

const UserInfoDropdown: FC<UserInfoDropdownProps> = ({
  teamName,
  scrolled,
  handleOpenUserComWidget,
  handleLogout,
  onClick,
}) => {
  const { t } = useTranslation();
  const classes = useStyles(scrolled)();
  const isMobile = !useIsWidthUp('xl');
  const { userInfo } = useContext(UserInfoCtx);
  const userName = userInfo?.name;

  const [isDropdownOpen, setDropdownOpen] = useState<boolean>(false);

  const colorItem = avatarColors.find(
    (item) => userInfo?.id && item.ids.includes(userInfo?.id?.slice(-1)),
  );

  const toggleDropdown = () => {
    setDropdownOpen((prevState) => !prevState);
  };

  const closeDropdown = () => setDropdownOpen(false);

  const logoutHandler = () => {
    closeDropdown();
    handleLogout();
  };

  const handleClickContact = () => {
    handleOpenUserComWidget();
    closeDropdown();
  };

  return (
    <ClickAwayListener onClickAway={closeDropdown}>
      <div className={classes.dotsBtnWrapper}>
        <ProfileAvatar
          aria-label="account of current user"
          onClick={isMobile ? onClick : toggleDropdown}
          background={colorItem?.background}
          color={colorItem?.color}
        >
          {userName
            ?.split(' ')
            .map((item) => item.substr(0, 1))
            .join('')}
        </ProfileAvatar>
        <div
          aria-label="dropdown"
          className={cx(classes.dropdown, isDropdownOpen && classes.dropdownOpened)}
        >
          <div className={classes.dropdownContent}>
            <div className={classes.divider}>
              <Typography className={classes.username}>{userName}</Typography>
              {teamName && <Typography className={classes.team}>{teamName}</Typography>}
            </div>
            <div className={classes.linksWrapper}>
              {PROFILE_LINKS.map((item) => (
                <Link
                  key={item.url}
                  to={item.url}
                  className={classes.dropdownLink}
                  onClick={closeDropdown}
                >
                  {t(item.title)}
                </Link>
              ))}
              {
                // @ts-ignore
                !!window?.userengage && (
                  <div className={classes.dropdownLink} onClick={handleClickContact}>
                    {t('header.contact')}
                  </div>
                )
              }
            </div>
          </div>
          <div className={classes.logout}>
            <button onClick={logoutHandler} className={classes.dropdownLink}>
              {t('header.logout')}
            </button>
          </div>
        </div>
      </div>
    </ClickAwayListener>
  );
};

export default UserInfoDropdown;

const ProfileAvatar = styled('button', {
  shouldForwardProp: (prop) => prop !== 'background' && prop !== 'color',
})`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  font-weight: 700;
  font-size: 14px;
  background: ${({ background }) => background};
  color: ${({ color }) => color};
  line-height: 17px;
  cursor: pointer;

  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 54px;
    height: 54px;
    font-size: 16px;
  }
`;
